import ReactModal from "react-modal";
import {useState, useEffect} from "react";
import axios from 'axios';
import {CdaWebModalContent} from './cdawebmodal/cdawebmodalcontent.js';
import {CdaWebModalErrorBox} from './cdawebmodal/cdawebmodalerrorbox.js';
import {CDAERRORCODES} from './../constants.js';


/**
 * Modal dialog that displays CDAWeb modal for a given dataset.
 *
 * @param props.title String title of modal.
 * @param props.url String where to get the data from.
 * @param props.cdasId String Id of dataset.
 * @param props.stpTime Datetime string that indicates the stop time of interest.
 * @param props.isCdaWebModalOpen Whether to display the modal
 * @param props.setCdaWebModalOpen Sets the value of isCdaWebModalOpen
 * @param props.sessionState Array of data that is used to save state of plots.
 * @param props.setSessionState Function that deals with setting the sessionState parameter.
 * @return JSX
 */
export function CdaWebModal(props) {
    const {title, url, stpTime, cdasId, isCdaWebModalOpen, setCdaWebModalOpen, sessionState, setSessionState} = props;
    const [modalError, setModalError] = useState(null);
    const [variables, setVariables] = useState([]);
    const [timelineInfo, setTimelineInfo] = useState([]);
    // eslint-disable-next-line
    const [filteredVariables, setFilteredVariables] = useState([]);

    const onRequestClose = () => {
        setCdaWebModalOpen(false);
    };

    useEffect(() => {
      const fetchVariablesFromCDAWeb = async (cdasId) => {
      try {
          const baseURL = 'https://cdaweb.gsfc.nasa.gov/WS/cdasr/1/';
          const varUrl = (baseURL +`dataviews/sp_phys/datasets/${cdasId}/variables`);
          const response = await axios.get(varUrl);
          setVariables(response.data.VariableDescription);
          const invUrl = (baseURL +`dataviews/sp_phys/datasets/${cdasId}/inventory`);
          //console.log(`CdaWeb checkpoint 0 ${invUrl}`);
          const resInventory = await axios.get(invUrl);
          //console.log(`CdaWeb checkpoint 1 ${JSON.stringify(resInventory.data?.InventoryDescription)}`);
          const timeIntervals = (resInventory.data?.InventoryDescription)[0].TimeInterval;
          //console.log(`CdaWeb checkpoint 2 ${timeIntervals}`);

          if (!Array.isArray(timeIntervals) || timeIntervals.length === 0) {
              const error = new Error("No TimeInterval data returned from CDAWeb inventory.");
              error.response = {
                  status: '007',
              };

              throw error;
          }

          const cdaWebStartTime = timeIntervals[0].Start.replaceAll('-','/').replace('T', ' ').replace('Z', '');
          const cdaWebEndTime = timeIntervals[timeIntervals.length-1].End.replaceAll('-','/').replace('T', ' ').replace('Z', '');
          setTimelineInfo({start: cdaWebStartTime, end: cdaWebEndTime});
      } catch (error) {
          setModalError({status: error?.response?.status, message: error.message});
      }
    };

      fetchVariablesFromCDAWeb(cdasId);
    }, [cdasId]);

    return (
        <ReactModal
          overlayClassName="cdaWebModalOverlay"
          className="cdaWebModalContent"
          contentLabel="CDA Modal"
          isOpen={isCdaWebModalOpen}
          onRequestClose={onRequestClose}>
          <div className="cdaWebModalContainer">
            <div className="cdaWebModalTopTitle">
              Get Data
            </div>
            <div className="cdaWebModalCloseButtonContainer">
              <button onClick={onRequestClose} className="cdaWebModalCloseButton">Close</button>
            </div>
            <br />
            {modalError &&
                 <CdaWebModalErrorBox title= {CDAERRORCODES['004']} body={modalError.message}/>
             }
            <CdaWebModalContent
              title = {title}
              timelineInfo = {timelineInfo}
              url = {url}
              variables = {filteredVariables.length > 0 ? filteredVariables : variables}
              cdasId= {cdasId}
              initialStartTime = {getUtcTimeOneDayBefore(timelineInfo.end ? timelineInfo.end : stpTime)}
              initialStopTime = {convertToValidTimeFormat(timelineInfo.end ? timelineInfo.end : stpTime)}
              sessionState= {sessionState}
              setSessionState= {setSessionState} />
          </div>
        </ReactModal>
    );
}


/**
 * Function that gets utc time down to seconds.
 *
 * @param props.datetimeString String datetime string.
 * @return String
 */
function convertToValidTimeFormat(datetimeString) {
    try{
        const date = new Date(datetimeString.replace(/-/g, '/').replace('T', ' ').split('.')[0]);
        if (isNaN(date.getTime())) {
            throw new Error('Invalid date format');
        }

        const utcTimestamp = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(),
                                  date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());

        const formattedDatetime = new Date(utcTimestamp);
        const year = formattedDatetime.getUTCFullYear();
        const month = String(formattedDatetime.getUTCMonth() + 1).padStart(2, '0');
        const day = String(formattedDatetime.getUTCDate()).padStart(2, '0');
        const hours = String(formattedDatetime.getUTCHours()).padStart(2, '0');
        const minutes = String(formattedDatetime.getUTCMinutes()).padStart(2, '0');
        const seconds = String(formattedDatetime.getUTCSeconds()).padStart(2, '0');

        const isoString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        return isoString;
    }  catch (error) {
        console.error(`Error processing date: ${error.message}`);
        return null;
    }
}


/**
 * Function that get utc time a day before 'datetimeString'.
 *
 * @param props.datetimeString String datetime string that is to be time shifted
 * @return String
 */
function getUtcTimeOneDayBefore(datetimeString) {
    try {
        const date = new Date(datetimeString.replace(/-/g, '/').replace('T', ' ').split('.')[0]);

        if (isNaN(date.getTime())) {
            throw new Error('Invalid date format');
        }
        const utcTimestamp = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(),
                                  date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());

        const utcOneDayBeforeTimestamp = utcTimestamp - 86400 * 1000;
        const utcOneDayBefore = new Date(utcOneDayBeforeTimestamp);

        const year = utcOneDayBefore.getUTCFullYear();
        const month = String(utcOneDayBefore.getUTCMonth() + 1).padStart(2, '0');
        const day = String(utcOneDayBefore.getUTCDate()).padStart(2, '0');
        const hours = String(utcOneDayBefore.getUTCHours()).padStart(2, '0');
        const minutes = String(utcOneDayBefore.getUTCMinutes()).padStart(2, '0');
        const seconds = String(utcOneDayBefore.getUTCSeconds()).padStart(2, '0');

        const isoString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        return isoString;
    } catch (error) {
        console.error(`Error processing date: ${error.message}`);
        return null;
    }
}

